import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ClipboardList, Loader2, ScanSearch, ArrowRight, AlertTriangle, CheckCircle2,
  Clock, TrendingUp, Sparkles, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, getScanIds, getRepKeys } from "@/lib/api";
import { BOARD_COLUMNS, STATUS_STYLE, urgencyRank } from "@/lib/constants";
import { TaskCard } from "@/components/tasks/TaskCard";

export default function Tasks() {
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [newIssues, setNewIssues] = useState(0);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const load = async () => {
      const ids = getScanIds();
      const repKeys = getRepKeys();
      if (!ids.length && !repKeys.length) { setLoading(false); return; }
      try {
        let scanKeys = [];
        let hist = { data: { businesses: [] } };
        if (ids.length) {
          const batch = await api.post("/scans/batch", { ids });
          scanKeys = (batch.data.scans || []).map((s) => s.business_key).filter(Boolean);
          hist = await api.post("/scans/history", { ids });
        }
        const bkeys = [...new Set([...scanKeys, ...repKeys])];
        setKeys(bkeys);
        if (!bkeys.length) { setLoading(false); return; }
        const [tl, st] = await Promise.all([
          api.post("/tasks/list", { business_keys: bkeys }),
          api.post("/tasks/stats", { business_keys: bkeys }),
        ]);
        setTasks(tl.data.tasks || []);
        setStats(st.data);
        setNewIssues((hist.data.businesses || []).reduce((a, b) => a + ((b.change?.new_opportunities?.length) || 0), 0));
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const refreshStats = async () => {
    if (!keys.length) return;
    try { const st = await api.post("/tasks/stats", { business_keys: keys }); setStats(st.data); } catch (e) { /* ignore */ }
  };
  const updateTask = async (id, patch) => {
    try {
      const { data } = await api.patch(`/tasks/${id}`, patch);
      setTasks((ts) => ts.map((t) => (t.public_id === id ? data.task : t)));
      refreshStats();
    } catch (e) { /* ignore */ }
  };
  const addNote = async (id, note) => {
    try {
      const { data } = await api.post(`/tasks/${id}/notes`, note);
      setTasks((ts) => ts.map((t) => (t.public_id === id ? data.task : t)));
    } catch (e) { /* ignore */ }
  };

  const active = tasks.filter((t) => t.status !== "DISMISSED" && !(t.status === "DONE" && t.verified));
  const archived = tasks.filter((t) => t.status === "DISMISSED" || (t.status === "DONE" && t.verified));
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Scan → Fix → Track → Verify</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Your Fix Plan</h1>
            <p className="mt-2 text-slate-600">Every "Fix This" opportunity becomes a trackable task. Move it across the board and verify the fix when it's done.</p>
          </div>
          <Button onClick={() => nav("/dashboard")} data-testid="tasks-to-dashboard"
            className="rounded-lg bg-white border border-slate-200 hover:border-slate-300 text-slate-800 font-medium px-5">
            View Scans
          </Button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-24"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
        ) : !tasks.length ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-14 text-center">
            <span className="grid place-items-center w-14 h-14 rounded-xl bg-slate-900 text-white mx-auto mb-5"><ClipboardList className="w-7 h-7" /></span>
            <h3 className="font-heading text-xl font-semibold text-slate-900">No tasks yet</h3>
            <p className="mt-2 text-slate-500 max-w-md mx-auto">Open a scan report and click <b>Fix This</b> on any opportunity to add it here as a trackable task.</p>
            <Button onClick={() => nav("/scan")} data-testid="tasks-empty-scan"
              className="mt-6 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6">
              Run a Scan <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : (
          <>
            {/* ROI Recovery Tracker */}
            {stats && <ROITracker stats={stats} />}

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-10">
              <Stat icon={Sparkles} label="Opportunities tracked" value={stats?.tracked ?? 0} />
              <Stat icon={TrendingUp} label="Est. opportunity (owner)" value={money(stats?.estimated_opportunity)} sub="estimated" color="#0f172a" />
              <Stat icon={CheckCircle2} label="Value recovered (confirmed)" value={money(stats?.confirmed_recovery)} sub="owner-confirmed" color="#059669" />
              <Stat icon={AlertTriangle} label="High-priority open" value={stats?.high_priority_unresolved ?? 0} color="#dc2626" />
              <Stat icon={CheckCircle2} label="Completed" value={stats?.completed ?? 0} color="#059669" />
              <Stat icon={Clock} label="Overdue" value={stats?.overdue ?? 0} color="#ea580c" />
              <Stat icon={ScanSearch} label="New on latest scan" value={newIssues} />
            </div>

            {/* Board */}
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="fix-board">
              {BOARD_COLUMNS.map((col) => {
                const colTasks = active.filter((t) => t.status === col)
                  .sort((a, b) => urgencyRank(a.urgency) - urgencyRank(b.urgency));
                const s = STATUS_STYLE[col];
                return (
                  <div key={col} className="min-w-0" data-testid={`board-col-${col.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-3 px-1">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-2.5 py-1 ${s.chip}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {col}
                      </span>
                      <span className="text-xs text-slate-400 font-medium">{colTasks.length}</span>
                    </div>
                    <div className="space-y-3">
                      {colTasks.map((t, i) => (
                        <motion.div key={t.public_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.03 }}>
                          <TaskCard task={t} index={`${col}-${i}`} onUpdate={updateTask} onAddNote={addNote} />
                        </motion.div>
                      ))}
                      {!colTasks.length && (
                        <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-xs text-slate-400">Nothing here</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Archived / history */}
            {archived.length > 0 && (
              <div className="mt-12">
                <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived"
                  className="inline-flex items-center gap-2 font-heading font-semibold text-slate-900">
                  {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  History &amp; archived <span className="text-slate-400 font-normal">({archived.length})</span>
                </button>
                {showArchived && (
                  <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {archived.map((t, i) => {
                      const variance = (t.verified && t.recovered_value != null && t.estimated_value != null)
                        ? Math.round(t.recovered_value - t.estimated_value) : null;
                      return (
                        <div key={t.public_id} className="rounded-xl border border-slate-200 bg-white p-4 opacity-90">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-[10px] font-semibold rounded-full border px-2 py-0.5 ${STATUS_STYLE[t.status].chip}`}>
                              {t.verified ? "VERIFIED" : t.status}
                            </span>
                            {t.recovered_value != null ? <span className="text-xs font-semibold text-emerald-600">{money(t.recovered_value)} recovered</span> : null}
                          </div>
                          <p className="text-sm font-medium text-slate-800">{t.problem}</p>
                          {variance != null && (
                            <p data-testid={`task-variance-${i}`} className="text-[11px] text-slate-500 mt-1.5">
                              Confirmed {money(t.recovered_value)} vs modeled {money(t.estimated_value)} · variance{" "}
                              <b className={variance < 0 ? "text-red-600" : "text-emerald-600"}>{variance < 0 ? "-" : "+"}{money(Math.abs(variance))}</b>
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, color = "#0f172a" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <Icon className="w-4 h-4 text-slate-400 mb-2" />
      <div className="font-heading text-2xl font-bold leading-none" style={{ color }}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1.5 leading-tight">{label}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide">{sub}</div>}
    </div>
  );
}


function ROITracker({ stats }) {
  const s = stats || {};
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;
  const pctCompleted = Math.min(100, s.recovered_pct || 0);
  const pctConfirmed = Math.min(100, s.confirmed_pct || 0);
  const variance = s.variance || 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 text-white p-6 sm:p-8 mb-8 shadow-lg" data-testid="roi-tracker">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-5 h-5 text-emerald-300" />
        <h2 className="font-heading text-xl font-bold">ROI Recovery Tracker</h2>
      </div>
      <p className="text-white/60 text-sm mb-6">Modeled opportunity you're capturing as you complete fixes. Confirmed recovery reflects owner-verified results only — never presented as guaranteed revenue.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <RoiTile testid="roi-estimated" label="Estimated opportunity" sub="MODELED" value={money(s.estimated_opportunity)} accent="text-white" />
        <RoiTile testid="roi-completed" label="Completed opportunity" sub="fixes marked done" value={money(s.completed_opportunity)} accent="text-indigo-300" />
        <RoiTile testid="roi-confirmed" label="Confirmed recovery" sub="owner-verified" value={money(s.confirmed_recovery)} accent="text-emerald-300" />
        <RoiTile testid="roi-pending" label="Pending verification" sub="not yet measured" value={money(s.pending_verification)} accent="text-amber-300" />
      </div>
      <div className="h-3 rounded-full bg-white/10 overflow-hidden flex" data-testid="roi-progress">
        <div className="h-full bg-emerald-400 transition-all" style={{ width: `${pctConfirmed}%` }} />
        <div className="h-full bg-indigo-400/70 transition-all" style={{ width: `${Math.max(0, pctCompleted - pctConfirmed)}%` }} />
      </div>
      <div className="flex flex-wrap justify-between gap-2 text-[11px] text-white/60 mt-2">
        <span>{pctCompleted}% of modeled opportunity addressed</span>
        <span>
          {pctConfirmed}% confirmed recovered
          {variance ? ` · variance ${variance < 0 ? "-" : "+"}$${Math.abs(variance).toLocaleString()} vs estimate` : ""}
        </span>
      </div>
      <p className="text-[11px] text-white/40 mt-3">Modeled figures are estimates, not guaranteed revenue or savings. Enter a confirmed result on each done fix to track actual recovery and variance.</p>
    </div>
  );
}

function RoiTile({ label, sub, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-white/10 border border-white/10 px-4 py-3">
      <div className={`font-heading text-2xl font-bold leading-none ${accent}`}>{value}</div>
      <div className="text-[11px] text-white/70 mt-1.5 leading-tight">{label}</div>
      <div className="text-[10px] uppercase tracking-wide text-white/40 mt-0.5">{sub}</div>
    </div>
  );
}
